import { makeAutoObservable } from 'mobx';
import { IOAuthCredentialsParams } from '../../../../../models/IntegrationsTypes';
import { ICredentialsStore } from '../integration-store';

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

export interface IBrightpearlCredentials {
    refund_method_code: string
    account_id: string
    new_sales_credit_status_id: number;
    external_reference_prefix: string;
    new_sales_credit_channel_name: string;
}

const CLOSE_INTERVAL = 50;

class BrightpearlOauthCredentialsStore implements ICredentialsStore {
    private _window = {} as Window;
    private _changes: IBrightpearlCredentials = {} as IBrightpearlCredentials;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;

    constructor(private _credentialsParams: IOAuthCredentialsParams) {
        makeAutoObservable(this);
    }

    public get account_id() { return this._changes.account_id; }
    public set account_id(v: string) { this._changes.account_id = v; }

    public get refund_method_code() { return this._changes.refund_method_code; }
    public set refund_method_code(v: string) { this._changes.refund_method_code = v; }

    public get new_sales_credit_status_id() { return this._changes.new_sales_credit_status_id; }
    public set new_sales_credit_status_id(v: number) { this._changes.new_sales_credit_status_id = v; }

    public get external_reference_prefix() { return this._changes.external_reference_prefix; }
    public set external_reference_prefix(v: string) { this._changes.external_reference_prefix = v; }

    public get newSalesCreditChannelName() { return this._changes.new_sales_credit_channel_name; }
    public set newSalesCreditChannelName(v: string) { this._changes.new_sales_credit_channel_name = v; }

    // Handle case when user didn't finish authorization flow and closed window
    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, CLOSE_INTERVAL);
    }

    private _handleMessage(event: IEventMessage, resolve: (value: unknown) => void, reject: (reason?: any) => void) {
        if (event.data?.type !== 'OAUTH_RESPOSE') {
            return;
        }
        this._window.close();
        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        if(event.data.payload?.state !== this._credentialsParams.state) {
            reject(new Error('Brightpearl state mismatch'));
        }

        if (event.data.payload?.code) {
            return resolve({ ...this._changes, code: event.data.payload.code });
        }

        return reject(new Error('Failed to receive code'));
    }

    /** ICredentialsStore implementation ************/
    public async getCredentials() {

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        return new Promise((resolve, reject) => {
            const params: Record<string, string> = {
                response_type: 'code',
                client_id: this._credentialsParams.client_id as string,
                redirect_uri: this._credentialsParams.callback_url as string,
                state: this._credentialsParams.state as string,
            };
            const urlSearchParams = new URLSearchParams(params);

            const oauthUrl = `https://oauth.brightpearl.com/authorize/${this._changes.account_id}?${urlSearchParams.toString()}`;
            this._window = window.open(oauthUrl, '', 'width=800,height=600,resizeable,scrollbars') as Window;

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, resolve, reject);
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }

    /********* ISaveable implementation *************/
    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {} as IBrightpearlCredentials;
    }
}

export default BrightpearlOauthCredentialsStore;
